import {Box} from '@mui/material';
import {DataGrid, GridColDef} from '@mui/x-data-grid';
import {useCallback, useMemo, useState} from 'react';
import {get_currency_mark, get_price_str} from 'common';
import OrderDetailDialog from './OrderDetailDialog';
import {IOrder} from 'types';

export default function OrderTable(props: {orders: IOrder[]}) {
	const {orders} = props;

	const get_profit_str = (value: number, exchange: string) => {
		return get_currency_mark(exchange) + ' ' + get_price_str(value, false, 0);
	};

	const columns = useMemo<GridColDef[]>(
		() => [
			{
				field: 'formattedDate',
				headerName: 'Date',
				width: 100,
			},
			{
				field: 'ticker',
				headerName: 'Sym',
				width: 100,
			},
			{
				field: 'exchange',
				headerName: 'Mkt',
				width: 100,
				renderCell: (params: any) => <div style={{textTransform: 'capitalize'}}>{params.row.exchange}</div>,
			},
			{
				field: 'profit',
				headerName: 'Profit',
				align: 'right',
				headerAlign: 'right',
				width: 130,
				renderCell: (params: any) => (
					<div style={{color: params.row.profit >= 0 ? '#2e7d32' : '#d32f2f', minWidth: '110px'}}>{get_profit_str(params.row.profit, params.row.exchange)}</div>
				),
			},
		],
		[],
	);

	const rows = useMemo(
		() =>
			orders.map(order => ({
				id: order.order_id,
				order_id: order.order_id,
				formattedDate: new Date(order.timestamp * 1000).toLocaleDateString('en-US'),
				ticker: order.ticker,
				exchange: order.exchange,
				profit: order.profit,
			})),
		[orders],
	);

	const orderMap = useMemo(() => {
		const map = new Map<string, IOrder>();
		orders.forEach(order => {
			map.set(order.order_id, order);
		});
		return map;
	}, [orders]);

	const [isDialogOpen, setDialogOpen] = useState(false);
	const [selectedOrder, setSelectedOrder] = useState<IOrder | null>(null);

	const handleRowClick = useCallback(
		(params: any) => {
			const order = orderMap.get(params.row.order_id);
			if (order) {
				setSelectedOrder(order);
				setDialogOpen(true);
			}
		},
		[orderMap],
	);

	const handleDialogClose = useCallback(() => {
		setDialogOpen(false);
		setSelectedOrder(null);
	}, []);

	return (
		<Box width="100%" height="auto" overflow="auto">
			<DataGrid
				rows={rows}
				columns={columns}
				initialState={{pagination: {paginationModel: {page: 0, pageSize: 10}}}}
				pageSizeOptions={[5, 10, 25]}
				localeText={{noRowsLabel: 'No orders found'}}
				columnHeaderHeight={40}
				onRowClick={handleRowClick}
			/>
			<OrderDetailDialog open={isDialogOpen} order={selectedOrder} onClose={handleDialogClose} />
		</Box>
	);
}
